import { Button, Popover, PopoverAnchor, PopoverContent } from '@ibsys/kit';
import { FC, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTablePresets } from '@/entities/settings';

type Props = {
  /**
   * Ключ рабочей записи в `settings.tables`: имя таблицы (например, `'dialogsListV2'`) либо
   * ключ раздела — у таблицы с разделами активный пресет и вид свои у каждого из них.
   */
  sectionKey: string;
  /** Элемент-якорь, рядом с которым появляется окно (кнопка пресетов) */
  children: ReactNode;
};

/**
 * Окно сохранения донастроенного системного пресета личной копией с отказом от донастройки.
 *
 * Показывается, пока пользователь без разрешения на управление системными пресетами держит
 * применённый системный пресет донастроенным: сам пресет общий, записать в него донастройку
 * такому пользователю нечем — распоряжаться он может только своей копией. Копия сохраняется
 * из текущего вида и сразу становится активной, поэтому окно исчезает само; отмена изменений
 * закрывает его вторым исходом — вид возвращается к пресету, а окно перекрывает строку
 * управления списком, где эта же отмена живёт для всех остальных случаев.
 */
export const SystemPresetCopyPopover: FC<Props> = ({ sectionKey, children }) => {
  const { t } = useTranslation('tablePresets');
  const { activePreset, isDirty, savePresetAs, apply } = useTablePresets(sectionKey);

  const isCopyOffered = Boolean(activePreset?.isSystem) && !activePreset?.isEditable && isDirty;

  const handleSaveCopy = useCallback(() => {
    if (!activePreset) return;
    savePresetAs(t('systemPresetCopySuffix', { name: activePreset.name }));
  }, [activePreset, savePresetAs, t]);

  // Отказ от донастройки: применяем пресет заново — вид возвращается к нему, сам пресет
  // остаётся применённым, копия не создаётся, и окно исчезает вместе с донастройкой.
  const handleUndoChanges = useCallback(() => {
    if (!activePreset) return;
    apply(activePreset.id);
  }, [activePreset, apply]);

  if (!isCopyOffered) return <>{children}</>;

  return (
    <Popover open modal={false}>
      {/* Якорь оборачивает кнопку — без min-w-0 обёртка не даёт ей сжаться по доступному месту. */}
      <PopoverAnchor className="min-w-0">{children}</PopoverAnchor>
      {/* Ширина держит оба действия в один ряд: подписи кнопок не переносятся, и в узком окне ряд
          выходил за его левую границу — отмена оказывалась обрезанной. */}
      <PopoverContent
        align="end"
        side="bottom"
        className="w-96 p-3 flex flex-col gap-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <p className="text-sm text-muted-foreground">{t('systemPresetCopyHint')}</p>
        {/* Перенос — страховка на более длинные подписи: действие уходит на свою строку, а не под обрез. */}
        <div className="flex flex-wrap gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={handleUndoChanges}>
            {t('undoChanges')}
          </Button>
          <Button size="sm" onClick={handleSaveCopy}>
            {t('systemPresetCopy')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
